import {
  AdminBulkReplayCapturedEvents,
  AdminDiscardCapturedEvent,
  AdminFetchCapturedEvents,
  AdminFetchKillswitchFlags,
  AdminReplayAllCapturedEvents,
  AdminReplayCapturedEvent,
  AdminToggleKillswitch,
} from 'src/constants/actionTypes'
import { KillswitchFeature } from 'src/constants/payments-ops/enums'
import type {
  CapturedEvent,
  FetchCapturedEventsPayload,
  KillswitchFlagsByFeature,
} from 'src/interfaces/usBankKillswitch'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { UsBankKillswitchState } from './types'

const buildCapturedEventsKey = (payload?: FetchCapturedEventsPayload): string => {
  return `${payload?.status ?? ''}|${payload?.source ?? ''}`
}

const getInitialState = (): UsBankKillswitchState => {
  return {
    flags: {
      [KillswitchFeature.PaymentProcessing]: [],
      [KillswitchFeature.Webhooks]: [],
    },
    flagsLoaded: false,
    flagsLoading: false,
    flagsError: null,
    toggling: false,
    capturedEvents: [],
    capturedEventsLoadedKey: null,
    capturedEventsRequestedKey: null,
    capturedEventsLoading: false,
    capturedEventsError: null,
    replaying: false,
  }
}

const REPLAY_REQUEST_TYPES = [
  AdminReplayCapturedEvent.type.REQUEST,
  AdminDiscardCapturedEvent.type.REQUEST,
  AdminBulkReplayCapturedEvents.type.REQUEST,
  AdminReplayAllCapturedEvents.type.REQUEST,
]

const REPLAY_SETTLED_TYPES = [
  AdminReplayCapturedEvent.type.SUCCESS,
  AdminReplayCapturedEvent.type.FAILED,
  AdminDiscardCapturedEvent.type.SUCCESS,
  AdminDiscardCapturedEvent.type.FAILED,
  AdminBulkReplayCapturedEvents.type.SUCCESS,
  AdminBulkReplayCapturedEvents.type.FAILED,
  AdminReplayAllCapturedEvents.type.SUCCESS,
  AdminReplayAllCapturedEvents.type.FAILED,
]

export const usBankKillswitchReducer = (
  state: UsBankKillswitchState = getInitialState(),
  action: Action<unknown>,
): UsBankKillswitchState => {
  if (REPLAY_REQUEST_TYPES.includes(action.type)) {
    return { ...state, replaying: true }
  }

  if (REPLAY_SETTLED_TYPES.includes(action.type)) {
    return { ...state, replaying: false }
  }

  switch (action.type) {
    case AdminFetchKillswitchFlags.type.REQUEST:
      return { ...state, flagsLoading: true, flagsError: null }
    case AdminFetchKillswitchFlags.type.SUCCESS:
      return {
        ...state,
        flags: (action.payload as KillswitchFlagsByFeature | undefined) ?? getInitialState().flags,
        flagsLoaded: true,
        flagsLoading: false,
        flagsError: null,
      }
    case AdminFetchKillswitchFlags.type.FAILED:
      return {
        ...state,
        flagsLoading: false,
        flagsError: resolveErrorMessage(action.payload, 'Failed to load the killswitches'),
      }

    // The saga refetches the flags after a toggle; nothing is patched here.
    case AdminToggleKillswitch.type.REQUEST:
      return { ...state, toggling: true }
    case AdminToggleKillswitch.type.SUCCESS:
    case AdminToggleKillswitch.type.FAILED:
      return { ...state, toggling: false }

    case AdminFetchCapturedEvents.type.REQUEST: {
      const requestedKey = buildCapturedEventsKey(
        action.payload as FetchCapturedEventsPayload | undefined,
      )
      const isSameKey = requestedKey === state.capturedEventsLoadedKey

      return {
        ...state,
        capturedEvents: isSameKey ? state.capturedEvents : [],
        capturedEventsLoadedKey: isSameKey ? state.capturedEventsLoadedKey : null,
        capturedEventsRequestedKey: requestedKey,
        capturedEventsLoading: true,
        capturedEventsError: null,
      }
    }
    case AdminFetchCapturedEvents.type.SUCCESS:
      return {
        ...state,
        capturedEvents: (action.payload as CapturedEvent[] | undefined) ?? [],
        capturedEventsLoadedKey: state.capturedEventsRequestedKey,
        capturedEventsLoading: false,
        capturedEventsError: null,
      }
    case AdminFetchCapturedEvents.type.FAILED:
      return {
        ...state,
        capturedEventsLoading: false,
        capturedEventsError: resolveErrorMessage(action.payload, 'Failed to load captured events'),
      }

    default:
      return state
  }
}
